// Want List web API client (ADR-0009). Backs the /u/@handle/wantlist page and
// the in-binder star. Authenticated by the caller's Firebase ID token; the
// server enforces the access gate and owner-only writes.

import { API_ENDPOINTS } from './endpoints.js';
import { apiResult } from './api-fetch.js';

/** @typedef {import('../types.js').WantListEntry} WantListEntry */
/** @typedef {WantListEntry & { id: number }} StoredWantListEntry */

/**
 * THE call to the want_list endpoint — every entry point below goes through
 * it. Never rejects: a network failure resolves to `{ ok: false, status: 0 }`,
 * anything that came back from the server resolves to its status plus whatever
 * the parsed body carried, so callers branch on `ok` and nothing else. The
 * failure arm keeps `data`/`error` because some failures are informative (a
 * 409 `cap_full` ships the caller's current `projects`); success-path callers
 * read `data` exactly as before.
 * @template T
 * @param {'GET' | 'POST' | 'PATCH' | 'DELETE'} method
 * @param {{ query?: string, body?: object }} [req]
 * @returns {Promise<import('../types.js').ApiResult<T>>}
 */
function request(method, { query = '', body } = {}) {
  return apiResult(`${API_ENDPOINTS.want_list}${query}`, { method, json: body });
}

/**
 * Fetch a want page's entries + viewer-relative availability. The group
 * picker's scope narrows the holders to that Reach on the server; the caller
 * resolves it with `selectedServerGroupId()` / `activeSubgroupIds()` from
 * `stores/browse.svelte.js`, and a null group sends no scope (every holder
 * the viewer may see).
 * @param {string} handle - bare username or 'me'
 * @param {{ groupId?: string | null, subgroupIds?: string[] }} [scope]
 * @returns {Promise<import('../types.js').ApiResult<import('../types.js').WantListPage>>}
 */
export function fetchWantList(handle, { groupId = null, subgroupIds = [] } = {}) {
  const sp = new URLSearchParams({ handle: `@${handle || 'me'}` });
  if (groupId) {
    sp.set('groupId', groupId);
    if (subgroupIds.length > 0) sp.set('subgroupIds', subgroupIds.join(','));
  }
  return request('GET', { query: `?${sp.toString()}` });
}

/**
 * Fetch a User Page (`?resource=profile`): identity, Shared groups, binders
 * with the viewer's want hits, and counts, shaped server-side for the viewer.
 * Same access gate as the want list: a 404 covers an unknown handle and an
 * owner outside the viewer's Network alike.
 * @param {string} handle - bare username or 'me'
 * @returns {Promise<import('../types.js').ApiResult<import('../types.js').UserProfile>>}
 */
export function fetchUserProfile(handle) {
  return request('GET', {
    query: `?resource=profile&handle=@${encodeURIComponent(handle || 'me')}`,
  });
}

/**
 * Every set one card name was printed in (`?resource=sets`), newest first,
 * with the finishes available in each: the card options modal's set
 * dropdown. No cap (Island has 221 sets). Symbols come from
 * `set-symbols.js` by `set_code`, never from this payload.
 * @param {string} name
 * @returns {Promise<import('../types.js').ApiResult<{ name: string, sets: import('../types.js').CardSet[] }>>}
 */
export function fetchCardSets(name) {
  return request('GET', { query: `?resource=sets&name=${encodeURIComponent(name)}` });
}

/**
 * One card name's printings (`?resource=printings`), each with its
 * `scryfall_id` and its finish's price: the card options modal's image
 * grid. With no set, the name's newest 40 (AVAILABLE_PRINTINGS_CAP); with
 * one (`&set=`), every printing of the name in that set, uncapped.
 * @param {string} name
 * @param {string | null} [setCode]
 * @returns {Promise<import('../types.js').ApiResult<import('../types.js').Printing[]>>}
 */
export async function fetchCardPrintings(name, setCode = null) {
  const set = setCode ? `&set=${encodeURIComponent(setCode)}` : '';
  /** @typedef {import('../types.js').Printing} Printing */
  /** @type {import('../types.js').ApiResult<{ name: string, printings: Printing[] }>} */
  const res = await request('GET', {
    query: `?resource=printings&name=${encodeURIComponent(name.trim())}${set}`,
  });
  if (res.ok === false) return res;
  return { ok: true, status: res.status, data: res.data?.printings || [] };
}

/**
 * Add an entry for the caller (manual add or star). The server answers with
 * the new entry's id only, not the enriched row.
 * @param {WantListEntry & { source?: string }} entry
 * @returns {Promise<import('../types.js').ApiResult<{ id: number }>>}
 */
export function addWantEntry(entry) {
  return request('POST', { body: entry });
}

/**
 * Remove a single entry by id.
 * @param {number} entryId
 * @returns {Promise<import('../types.js').ApiResult<unknown>>}
 */
export function removeWantEntry(entryId) {
  return request('DELETE', { body: { entry_id: entryId } });
}

/**
 * Remove every entry matching a concrete card (the star un-toggle).
 * @param {WantListEntry} card
 * @returns {Promise<import('../types.js').ApiResult<unknown>>}
 */
export function removeWantEntriesMatching(card) {
  return request('DELETE', { body: card });
}

/**
 * Re-point an entry to a new Card Specificity in place (the card options
 * modal), in either direction. A SINGLE atomic PATCH to the want_list endpoint: the backend
 * re-points the existing entry, preserving its id — no add-then-remove race —
 * and returns the updated entry so callers can apply it optimistically.
 * Passing all-null narrowing dimensions broadens toward name-level.
 * @param {{ id: number }} entry the current entry (only `id` is used)
 * @param {{ set_code?: string|null, collector_number?: string|null, finish?: string|null }} spec
 * @returns {Promise<import('../types.js').ApiResult<import('../types.js').WantEntry>>}
 */
export function setEntrySpecificity(entry, spec) {
  return request('PATCH', {
    body: {
      entry_id: entry.id,
      set_code: spec.set_code || null,
      collector_number: spec.collector_number || null,
      finish: spec.finish || null,
    },
  });
}

// ---------------------------------------------------------------------------
// Projects (ADR-0016): external decks/cubes registered as sources for a want
// list, alongside the caller's own binders.
// ---------------------------------------------------------------------------

const PROJECTS_QUERY = '?resource=projects';

/**
 * Register a deck/cube link as one of the caller's Projects (ADR-0016).
 * `error` is 'cap_full' at the cap (409) or a message for a bad/unfetchable
 * link (400); `data` is `{ registration, projects }` — and on a 409 it still
 * carries the caller's real `projects`, so the picker re-renders the true
 * count.
 * @param {string} url - full deck/cube URL or short form
 * @returns {Promise<import('../types.js').ApiResult<{ registration: object, projects: import('../types.js').Project[] }>>}
 */
export function addProject(url) {
  return request('POST', { query: PROJECTS_QUERY, body: { url } });
}

/**
 * Remove one of the caller's Projects. `data` is `{ removed, projects }`.
 * @param {string} projectBinderId
 * @returns {Promise<import('../types.js').ApiResult<{ removed: boolean, projects: import('../types.js').Project[] }>>}
 */
export function removeProject(projectBinderId) {
  return request('DELETE', {
    query: PROJECTS_QUERY,
    body: { project_binder_id: projectBinderId },
  });
}
